import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LineChart, TrendingUp, History, Zap, ArrowRight, Bell, BookOpen } from 'lucide-react';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import OnboardingTour from '../components/ui/OnboardingTour';
import { useAuthStore } from '../store/authStore';
import { useAnalysisStore } from '../store/analysisStore';
import type { StoredAnalysisPayload } from '../store/analysisStore';
import { analysisService } from '../services/analysis.service';
import api from '../services/api';
import { formatRelativeTime, SUBSCRIPTION_PLANS, getSymbolName } from '@trading/shared';
import { logger } from '../lib/logger.js';
import { BRAND } from '../config/brand';

interface UserStats {
  totalAnalyses: number;
  todayAnalyses: number;
  distinctAssets: number;
  activeAlerts: number;
  signalsViewed: number;
  plan: string;
  limits: {
    analysesPerDay: number;
    assetsLimit: number | null;
    alertsLimit: number;
    signalsLimit: number | null;
    historyDays: number;
    methodologies: string[];
    timeframes: string[];
  };
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { loadAnalysis } = useAnalysisStore();
  const navigate = useNavigate();
  const [recentAnalyses, setRecentAnalyses] = useState<StoredAnalysisPayload[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(() => {
    return localStorage.getItem(`${BRAND.storagePrefix}_onboarding_completed`) !== 'true';
  });

  const plan = user?.subscription?.plan || 'free';
  const planConfig = SUBSCRIPTION_PLANS[plan];

  const handleAnalysisClick = (analysis: StoredAnalysisPayload) => {
    loadAnalysis(analysis);
    navigate('/analysis');
  };

  useEffect(() => {
    Promise.all([
      analysisService.getHistory(1, 5).then((res) => setRecentAnalyses(res.items)),
      api.get<UserStats>('/user/stats').then((res) => setStats(res.data)),
    ])
      .catch((err) => logger.error('Failed to load dashboard data:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          {t('dashboard.welcome', { name: user?.name })}
        </h1>
        <p className="text-text-secondary mt-1">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Usage Banner */}
      {stats && plan === 'free' && (
        <Card className="p-4 border border-accent-primary/30 bg-accent-primary/5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-text-secondary">
                <span className="font-semibold text-text-primary">{stats.todayAnalyses}</span>
                /{stats.limits.analysesPerDay === -1 ? '\u221e' : stats.limits.analysesPerDay} {t('dashboard.analyses')}
              </span>
              <span className="text-border-subtle">|</span>
              <span className="text-text-secondary">
                <span className="font-semibold text-text-primary">{stats.distinctAssets}</span>
                /{stats.limits.assetsLimit ?? '\u221e'} {t('dashboard.assets')}
              </span>
              <span className="text-border-subtle">|</span>
              <span className="text-text-secondary">
                <span className="font-semibold text-text-primary">{stats.activeAlerts}</span>
                /{stats.limits.alertsLimit === -1 ? '\u221e' : stats.limits.alertsLimit} {t('dashboard.alerts')}
              </span>
            </div>
            <Link to="/pricing">
              <Button variant="primary" size="sm">
                {t('actions.upgrade')}
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* On wide screens, split into a main column (stats + actions) and an
          aside (recent analyses) so the dashboard fills the width instead of
          stacking in a narrow centered column. */}
      <div className="xl:grid xl:grid-cols-3 xl:gap-6 xl:items-start space-y-6 xl:space-y-0">
        <div className="xl:col-span-2 space-y-6">
      {/* Stats Grid */}
      <div data-tour="stats-grid" className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-primary/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">{t('dashboard.dailyAnalyses')}</p>
              <p className="text-xl font-bold text-text-primary">
                {stats?.todayAnalyses ?? 0} / {planConfig.analysesPerDay === -1 ? '\u221e' : planConfig.analysesPerDay}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">{t('dashboard.signalsViewed')}</p>
              <p className="text-xl font-bold text-text-primary">
                {stats?.signalsViewed ?? 0} / {planConfig.signalsLimit === null ? '\u221e' : planConfig.signalsLimit}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card padding="lg" data-tour="quick-analysis">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {t('dashboard.readyToAnalyze')}
              </h2>
              <p className="text-text-secondary text-sm">
                {t('dashboard.readyDescription')}
              </p>
            </div>
            <Link to="/analysis">
              <Button size="lg" className="gap-2 whitespace-nowrap">
                <LineChart className="w-5 h-5" />
                {t('actions.newAnalysis')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </Card>

        <Card padding="lg" data-tour="quick-journal">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {t('nav.journal')}
              </h2>
              <p className="text-text-secondary text-sm">
                {t('dashboard.journalDescription')}
              </p>
            </div>
            <Link to="/journal">
              <Button variant="secondary" size="lg" className="gap-2 whitespace-nowrap">
                <BookOpen className="w-5 h-5" />
                {t('dashboard.openJournal')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>

        </div>

        {/* Aside: recent analyses (fills the right side on wide screens) */}
        <div className="xl:col-span-1">
      {/* Recent Analyses */}
      <Card padding="lg" data-tour="recent-analyses">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <History className="w-5 h-5" />
            {t('dashboard.recentAnalyses')}
          </h2>
          <Link to="/history">
            <Button variant="ghost" size="sm">
              {t('actions.viewAll')}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-4 p-3 bg-bg-tertiary rounded-lg">
                <div className="w-10 h-10 bg-bg-elevated rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-bg-elevated rounded w-1/4" />
                  <div className="h-3 bg-bg-elevated rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : recentAnalyses.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <History className="w-12 h-12 mx-auto mb-3 text-text-muted" />
            <p>{t('dashboard.emptyState')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentAnalyses.map((analysis) => (
              <div
                key={analysis.id}
                onClick={() => handleAnalysisClick(analysis)}
                className="flex items-center gap-4 p-3 bg-bg-tertiary rounded-lg hover:bg-bg-elevated transition-colors cursor-pointer"
              >
                <div
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    ${analysis.direction === 'long' ? 'bg-profit/20' : 'bg-loss/20'}
                  `}
                >
                  <TrendingUp
                    className={`w-5 h-5 ${
                      analysis.direction === 'long' ? 'text-profit' : 'text-loss rotate-180'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">
                      {getSymbolName(analysis.symbol)}
                    </span>
                    <Badge
                      variant={analysis.direction === 'long' ? 'success' : 'danger'}
                      size="sm"
                    >
                      {analysis.direction}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary">
                    {analysis.timeframe} • {analysis.confidence}% {t('dashboard.confidence')}
                  </p>
                </div>
                <p className="text-sm text-text-tertiary">
                  {formatRelativeTime(new Date(analysis.createdAt))}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
        </div>
      </div>

      {showTour && user?.subscription?.status === 'trialing' && (
        <OnboardingTour onComplete={() => setShowTour(false)} />
      )}
    </div>
  );
}
